import { z } from 'zod/v4';
import { PlanResourceSchema } from './plan-resource.js';

export const LIST_PLANS_DEFAULT_PAGE_SIZE = 10;

/**
 * The largest page the list will serve. A bigger `count` is **clamped**, not
 * rejected — the dashboard has always answered 200 with a capped page, and a
 * 400 here would break callers that ask for more.
 */
export const LIST_PLANS_MAX_PAGE_SIZE = 100;

/**
 * The plans list query. Identical to the actions list query except for
 * `quickFilter`, which has no `reportedByMe` mode here: the plans SQL has never
 * implemented one, and accepting the value would silently return every plan.
 */
export const ListPlansQuerySchema = z.object({
  count: z
    .preprocess(
      val => val ?? undefined,
      z.coerce.number().int().min(1).optional(),
    )
    .default(LIST_PLANS_DEFAULT_PAGE_SIZE)
    .transform(value => Math.min(value, LIST_PLANS_MAX_PAGE_SIZE)),
  page: z
    .preprocess(
      val => val ?? undefined,
      z.coerce.number().int().min(0).optional(),
    )
    .default(0),
  sort: z.string().optional(),
  filter: z
    .string()
    .max(512)
    .optional()
    .describe('JSON string with filter criteria for plans'),
  quickFilter: z
    .enum(['all', 'internal', 'external', 'assignedToMe'])
    .optional()
    .default('all')
    .describe(
      "Quick filter options: 'all' for every accessible plan, 'internal' for plans owned by your company, 'external' for plans owned by other companies, 'assignedToMe' for plans assigned to you",
    ),
});

export const ListPlansResponseSchema = z.object({
  data: z.array(PlanResourceSchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

export type ListPlansQuery = z.infer<typeof ListPlansQuerySchema>;
export type ListPlansResponse = z.infer<typeof ListPlansResponseSchema>;
